/* ---------- Automationen: „Wenn … (unter Bedingungen) dann …“ ---------- */
/*
 * 0.59 (Paket A, Entwicklerbericht „Automationen gezielt erweitern“):
 * - Regelvertrag mit `schemaVersion` 2: Bedingungen als Gruppe (alle/mindestens eine, eine Ebene
 *   Untergruppen, höchstens 20 Prädikate). Regeln aus Version 1 (`conditions`-Liste) entsprechen
 *   einer „alle“-Gruppe und behalten ihre Bedeutung.
 * - Neue Auslöser (Label, Spalte verlassen, wieder geöffnet, Feld, Datei) entstehen serverseitig aus dem
 *   Vorher/Nachher-Abgleich jeder Karte – gleich für Web, App, Formulare, Offline und Regeln.
 * - Auswertung und Beschreibung hier gemeinsam für Server und Browser (Vorschau „An Karte prüfen“).
 * - 0.60: `mode: 'state'` („Sobald erfüllt“). 0.61: Termine (Auslöser, Bedingungen, Aktionen), Aufwand und
 *   Zeitjobs – Zeitsemantik in `automationTime.ts`.
 */
import { approvalOf, approvalStatus } from './approval.js';
import { HOLIDAY_REGION_NAME, type HolidayRegion } from './holidays.js';
import type { CustomField, FieldType } from './types.js';
import type { HintItem, HintTarget } from './context.js';
import type { ReminderOffset, ReminderRecipients } from './reminders.js';
import {
  DATE_NAME,
  dateOffsetText,
  minutesText,
  timePredicateMatches,
  unitText,
  type DateBase,
  type DateUnit,
  type DateWhich,
} from './automationTime.js';

export type FileKind = 'any' | 'image' | 'pdf' | 'other';

export type AutomationTrigger =
  | { type: 'moved'; columnId: string }
  | { type: 'created'; columnId: string | null }
  | { type: 'completed' }
  | { type: 'checklistDone' }
  | { type: 'overdue' }
  // 0.59
  | { type: 'left'; columnId: string }
  | { type: 'reopened' }
  /** leer = beliebiges Label */
  | { type: 'labelAdded'; labelIds: string[] }
  | { type: 'labelRemoved'; labelIds: string[] }
  /** optional eingegrenzt: Wert vorher (`from`) und nachher (`to`) */
  | { type: 'fieldChanged'; fieldId: string; from?: FieldTest | null; to?: FieldTest | null }
  | { type: 'fileAdded'; kind: FileKind; includeComments: boolean }
  | { type: 'fileRemoved'; kind: FileKind; includeComments: boolean }
  /** 0.60: „Sobald die Bedingungen erfüllt sind“ (Zustandsregel, `mode: 'state'`) */
  | { type: 'stateMet' }
  // 0.61: Termine und Aufwand
  | { type: 'dateChanged'; which: DateWhich; change: 'any' | 'set' | 'changed' | 'removed' }
  /**
   * Zeitpunkt relativ zu Start/Frist (geplanter Zeitjob). `repeat`: danach wiederholen (Stunden als Dauer,
   * Tage als Kalendertage), höchstens `max`-mal. `catchUpMinutes`: so lange nach einem Ausfall noch
   * nachholen (Standard 0 = verpasste Zeitpunkte nicht nachholen).
   */
  | {
      type: 'dateReached';
      which: DateWhich;
      /** 0.81: statt Start/Frist ein eigenes Datumsfeld (ganztägig, Bezug wie der Start: 9:00 Uhr) */
      fieldId?: string | null;
      offset: ReminderOffset;
      repeat?: { every: number; unit: 'hours' | 'days'; max: number } | null;
      catchUpMinutes?: number;
    }
  | { type: 'effortChanged'; what: 'estimate' | 'spent' }
  // 0.62: Abhängigkeiten (gleiche Startbereitschaft wie die Übergaben der Erinnerungen)
  | { type: 'predecessorsDone' }
  | { type: 'ready' }
  /** 0.63: Knopf an der Karte („Zur Freigabe vorbereiten“) – dieselben Bedingungen und Aktionen */
  | { type: 'manual'; label: string };

/** 0.63: Empfänger einer Nachricht (ohne Angabe: Zuständige wie bisher) */
export type NotifyRecipients = Pick<ReminderRecipients, 'assignees' | 'owner' | 'watchers' | 'users' | 'groups'>;

/** Bedingungen aus Version 1 (bleiben gültig) */
export type AutomationCondition =
  | { type: 'hasLabel'; labelId: string }
  | { type: 'notLabel'; labelId: string }
  | { type: 'priorityAtLeast'; value: number };

export type FieldTestOp =
  | 'empty'
  | 'filled'
  | 'is'
  | 'isNot'
  | 'contains'
  | 'startsWith'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'anyOf'
  | 'allOf'
  | 'noneOf'
  | 'before'
  | 'after'
  | 'checked'
  | 'unchecked'
  /** 0.80: Freigabe */
  | 'approved'
  | 'notApproved'
  | 'rejected';

/** Typgerechter Vergleich eines eigenen Feldes */
export interface FieldTest {
  op: FieldTestOp;
  value?: string | number | null;
  /** zweiter Wert für „zwischen“ */
  value2?: string | number | null;
  /** Auswahlfelder: Options-IDs */
  optionIds?: string[];
}

export type AutomationPredicate =
  | AutomationCondition
  | { type: 'labels'; mode: 'any' | 'all' | 'none'; labelIds: string[] }
  | { type: 'priority'; op: 'gte' | 'lte' | 'eq'; value: number }
  | { type: 'column'; mode: 'in' | 'notIn'; columnIds: string[] }
  | { type: 'done'; value: boolean }
  /** min 0 = „keine solche Datei“ */
  | { type: 'files'; kind: FileKind; includeComments: boolean; min: number }
  | { type: 'field'; fieldId: string; test: FieldTest }
  | { type: 'assignees'; mode: 'none' | 'some' | 'includes'; userId?: string }
  | { type: 'blocked'; value: boolean }
  // 0.60
  | { type: 'dates'; which: 'start' | 'due'; op: 'set' | 'missing' }
  | { type: 'checklist'; op: 'complete' | 'incomplete' | 'none' }
  // 0.61 – zeitabhängig: Termin erreicht/nicht erreicht, in den nächsten N Minuten, seit mindestens N Minuten
  | { type: 'dateWindow'; which: DateWhich; op: 'reached' | 'notReached' | 'within' | 'overBy'; minutes?: number }
  /** Aufwand: Prozent einer positiven Schätzung oder Minuten; optional mit laufenden Timern */
  | { type: 'effort'; measure: 'percent' | 'minutes'; op: 'gte' | 'lt'; value: number; includeRunning: boolean }
  | { type: 'estimate'; op: 'set' | 'missing' }
  // 0.62
  | { type: 'predecessors'; op: 'allDone' | 'open' | 'none' }
  | { type: 'ready'; value: boolean }
  /** Verweildauer seit dem letzten Eintritt in die aktuelle Spalte */
  | { type: 'dwell'; op: 'atLeast' | 'lessThan'; minutes: number }
  /** Zeit seit der letzten Bearbeitung durch eine Person (Automationen zählen nicht) */
  | { type: 'inactive'; op: 'atLeast' | 'lessThan'; minutes: number }
  /** 0.79: Vier-Augen-Prinzip – wer die Änderung auslöst (Regeln, Knöpfe, verbindliche Spaltenregeln) */
  | { type: 'actor'; mode: 'is' | 'isNot'; who: ActorRef };

/**
 * 0.79: Bezug für „Die auslösende Person ist (nicht) …“. `movedTo`: wer die Karte zuletzt in diese Spalte
 * gebracht hat (auch beim Anlegen dort); `fieldSetBy`: wer das Feld zuletzt gesetzt hat (Verlauf).
 */
export type ActorRef =
  | { kind: 'creator' }
  | { kind: 'owner' }
  | { kind: 'assignee' }
  | { kind: 'boardOwner' }
  | { kind: 'movedTo'; columnId: string }
  | { kind: 'fieldSetBy'; fieldId: string }
  /** 0.80: hat in diesem Freigabefeld freigegeben */
  | { kind: 'approvedBy'; fieldId: string }
  | { kind: 'user'; userId: string }
  | { kind: 'group'; groupId: string };

export const ACTOR_KINDS: ActorRef['kind'][] = ['creator', 'owner', 'assignee', 'boardOwner', 'movedTo', 'fieldSetBy', 'approvedBy', 'user', 'group'];
export const ACTOR_KIND_LABEL: Record<ActorRef['kind'], string> = {
  creator: 'wer die Karte angelegt hat',
  owner: 'hauptverantwortlich',
  assignee: 'zuständig',
  boardOwner: 'Besitzer*in des Boards',
  movedTo: 'wer die Karte in eine Spalte gebracht hat',
  fieldSetBy: 'wer ein Feld gesetzt hat',
  approvedBy: 'wer freigegeben hat',
  user: 'eine bestimmte Person',
  group: 'Mitglied einer Gruppe',
};

/** 0.79: Personen rund um eine Karte – nur gefüllt, wenn Bedingungen sie brauchen (`usesActor`) */
export interface AutomationPeople {
  /** auslösende Person; null = keine (Zeitpunkt, Formular, Automation ohne Person) */
  actor: string | null;
  creator: string | null;
  owner: string | null;
  boardOwners: string[];
  /** Spalte → wer die Karte zuletzt hineingebracht hat (null = Automation/System) */
  movedBy: Record<string, string | null>;
  /** Feld → wer es zuletzt gesetzt hat (null = Automation/System) */
  fieldSetBy: Record<string, string | null>;
  /** Teamgruppen der auslösenden Person */
  actorGroups: string[];
}

export interface ConditionGroup {
  op: 'all' | 'any';
  items: (AutomationPredicate | ConditionGroup)[];
}

export type AutomationAction =
  | { type: 'move'; columnId: string; placement: 'top' | 'bottom' }
  | { type: 'complete' }
  | { type: 'reopen' }
  /** userId 'actor' = die Person, die den Auslöser verursacht hat */
  | { type: 'assign'; userId: string }
  | { type: 'unassignAll' }
  | { type: 'addLabel'; labelId: string }
  | { type: 'removeLabel'; labelId: string }
  | { type: 'setDue'; days: number }
  | { type: 'clearDue' }
  | { type: 'setPriority'; value: number }
  | { type: 'addChecklist'; items: string[] }
  | { type: 'notify'; text: string; recipients?: NotifyRecipients }
  | { type: 'archive' }
  // 0.59
  | { type: 'labels'; add: string[]; remove: string[] }
  | { type: 'setField'; fieldId: string; value: string | number | boolean | string[] }
  | { type: 'clearField'; fieldId: string }
  | { type: 'fieldOptions'; fieldId: string; add: string[]; remove: string[] }
  // 0.61
  /**
   * Termin (Start, Frist oder Datumsfeld) setzen: Bezug (jetzt, Auslöser, Start, Frist, Datumsfeld, fester
   * Termin) plus Abstand. Kalendertage/Wochen behalten die Ortszeit; `time`: 'base' = wie der Bezug,
   * 'allDay' = ganztägig, sonst feste Uhrzeit HH:MM. Minuten/Stunden sind eine Dauer.
   */
  | {
      type: 'setDate';
      which: DateWhich | 'field';
      fieldId?: string;
      base: DateBase;
      baseFieldId?: string;
      baseDate?: string;
      baseTime?: string | null;
      amount: number;
      unit: DateUnit;
      /** 0.69: Arbeitstage – Feiertage dieses Bundeslands zählen nicht mit */
      holidays?: HolidayRegion | null;
      time: string;
      onlyIfEmpty: boolean;
    }
  | { type: 'shiftDate'; which: DateWhich; amount: number; unit: DateUnit; holidays?: HolidayRegion | null }
  | { type: 'clearDate'; which: DateWhich }
  | { type: 'setEstimate'; minutes: number | null }
  /** 0.81: Zahl rechnen – Wert oder anderes Feld addieren, abziehen, malnehmen oder übernehmen */
  | { type: 'calcField'; fieldId: string; op: 'add' | 'sub' | 'mul' | 'set'; value: number | null; fromFieldId?: string | null }
  /** 0.81: Folgekarte anlegen (dieses oder anderes Board); Titel mit {titel}, Übernahme gleichnamiger Felder/Labels */
  | {
      type: 'createCard';
      boardId: string | null;
      columnId: string;
      title: string;
      copy: { fields: boolean; labels: boolean; description: boolean; assignees: boolean };
    };

export type AutomationMode = 'event' | 'state';

export interface Automation {
  id: string;
  boardId: string;
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  /** Version-1-Liste; bei Regeln mit `schemaVersion` 2 leer */
  conditions: AutomationCondition[];
  /** Bedingungen als Gruppe (für Version-1-Regeln aus `conditions` abgeleitet) */
  when: ConditionGroup;
  actions: AutomationAction[];
  mode: AutomationMode;
  /** 0.60: Zustandsregel nur einmal je Karte (Zurücksetzen ist eine eigene Aktion) */
  once: boolean;
  /** 0.61: Zeitzone der Regel (Termine, Kalendertage) */
  timeZone: string;
  /** 0.61: geplante Zeitpunkte (Termin-Auslöser und Neuprüfungen) */
  planned?: { count: number; next: string | null; missed: number };
  schemaVersion: number;
  /** zählt jede Änderung (für `expectedRevision`) */
  revision: number;
  runCount: number;
  lastRunAt: string | null;
  lastError: string | null;
  /** Verweise, die es nicht mehr gibt – die Regel läuft nicht, bis sie repariert ist */
  problems: string[];
}

export const AUTOMATION_SCHEMA_VERSION = 2;
export const AUTOMATION_MAX_PREDICATES = 20;
export const AUTOMATION_MAX_ACTIONS = 8;
/** 0.61: größter Abstand je Einheit bei Terminaktionen */
/** „ (Arbeitstage ohne Feiertage in Bayern)“ */
const workdayNote = (a: { unit: DateUnit; holidays?: HolidayRegion | null }) =>
  a.unit !== 'workdays' ? '' : a.holidays ? ` (ohne Wochenenden und Feiertage in ${HOLIDAY_REGION_NAME[a.holidays]})` : ' (ohne Wochenenden)';

export const AMOUNT_MAX: Record<DateUnit, number> = { minutes: 60 * 24 * 60, hours: 24 * 366, days: 366 * 3, weeks: 156, workdays: 260 * 3 };

export const TRIGGER_TYPES: AutomationTrigger['type'][] = [
  'stateMet',
  'created',
  'moved',
  'left',
  'completed',
  'reopened',
  'labelAdded',
  'labelRemoved',
  'fieldChanged',
  'fileAdded',
  'fileRemoved',
  'checklistDone',
  'dateChanged',
  'dateReached',
  'overdue',
  'effortChanged',
  'predecessorsDone',
  'ready',
  'manual',
];

/** Gruppierung im Editor (durchsuchbar nach Kategorien) */
export const TRIGGER_GROUPS: { label: string; types: AutomationTrigger['type'][] }[] = [
  { label: 'Zustand', types: ['stateMet'] },
  { label: 'Manuell', types: ['manual'] },
  { label: 'Karte', types: ['created', 'moved', 'left', 'completed', 'reopened', 'checklistDone'] },
  { label: 'Labels', types: ['labelAdded', 'labelRemoved'] },
  { label: 'Eigene Felder', types: ['fieldChanged'] },
  { label: 'Dateien', types: ['fileAdded', 'fileRemoved'] },
  { label: 'Termine', types: ['dateReached', 'dateChanged', 'overdue'] },
  { label: 'Aufwand', types: ['effortChanged'] },
  { label: 'Abhängigkeiten', types: ['predecessorsDone', 'ready'] },
];

export const ACTION_TYPES: AutomationAction['type'][] = [
  'move',
  'complete',
  'reopen',
  'assign',
  'unassignAll',
  'labels',
  'addLabel',
  'removeLabel',
  'setField',
  'clearField',
  'fieldOptions',
  'setDate',
  'shiftDate',
  'clearDate',
  'setDue',
  'clearDue',
  'setEstimate',
  'setPriority',
  'addChecklist',
  'notify',
  'archive',
  'calcField',
  'createCard',
];

/** Aktionen, die der Editor neu anbietet (addLabel/removeLabel gehen in „labels“ auf, bleiben aber gültig) */
export const ACTION_TYPES_EDITOR: AutomationAction['type'][] = ACTION_TYPES.filter((t) => t !== 'addLabel' && t !== 'removeLabel' && t !== 'setDue' && t !== 'clearDue');

export const TRIGGER_LABEL: Record<AutomationTrigger['type'], string> = {
  moved: 'eine Karte in eine Spalte kommt',
  created: 'eine Karte angelegt wird',
  completed: 'eine Karte erledigt wird',
  checklistDone: 'die Checkliste komplett abgehakt ist',
  overdue: 'die Frist überschritten ist',
  left: 'eine Karte eine Spalte verlässt',
  reopened: 'eine Karte wieder geöffnet wird',
  labelAdded: 'ein Label hinzugefügt wird',
  labelRemoved: 'ein Label entfernt wird',
  fieldChanged: 'ein eigenes Feld geändert wird',
  fileAdded: 'eine Datei verfügbar wird',
  fileRemoved: 'eine Datei entfernt wird',
  stateMet: 'Sobald die Bedingungen erfüllt sind',
  dateChanged: 'Start oder Frist geändert wird',
  dateReached: 'Start oder Frist erreicht ist (oder davor/danach)',
  effortChanged: 'Schätzung oder gebuchte Zeit sich ändert',
  predecessorsDone: 'alle erforderlichen Vorgänger erledigt sind',
  ready: 'eine Karte startbereit wird',
  manual: 'jemand den Knopf an der Karte wählt',
};

export const ACTION_LABEL: Record<AutomationAction['type'], string> = {
  move: 'in Spalte verschieben',
  complete: 'als erledigt markieren',
  reopen: 'wieder öffnen',
  assign: 'Person zuweisen',
  unassignAll: 'alle Zuständigen entfernen',
  addLabel: 'Label hinzufügen',
  removeLabel: 'Label entfernen',
  setDue: 'Frist setzen',
  clearDue: 'Frist entfernen',
  setPriority: 'Priorität setzen',
  addChecklist: 'Checkliste ergänzen',
  notify: 'Nachricht senden',
  archive: 'archivieren',
  labels: 'Labels ergänzen/entfernen',
  setField: 'eigenes Feld setzen',
  clearField: 'eigenes Feld leeren',
  fieldOptions: 'Auswahlwerte ergänzen/entfernen',
  setDate: 'Start/Frist setzen',
  shiftDate: 'Start/Frist verschieben',
  clearDate: 'Start/Frist entfernen',
  setEstimate: 'Schätzung setzen',
  calcField: 'Zahl rechnen',
  createCard: 'Folgekarte anlegen',
};

export const PREDICATE_TYPES = ['labels', 'column', 'done', 'priority', 'assignees', 'blocked', 'dates', 'dateWindow', 'checklist', 'effort', 'estimate', 'predecessors', 'ready', 'dwell', 'inactive', 'files', 'field', 'actor'] as const;
export const PREDICATE_LABEL: Record<(typeof PREDICATE_TYPES)[number], string> = {
  labels: 'Labels',
  column: 'Spalte',
  done: 'Erledigt',
  priority: 'Priorität',
  assignees: 'Zuständige',
  blocked: 'Blockiert',
  files: 'Dateien',
  field: 'Eigenes Feld',
  dates: 'Start/Frist',
  checklist: 'Checkliste',
  dateWindow: 'Start/Frist erreicht',
  effort: 'Aufwand',
  estimate: 'Schätzung',
  predecessors: 'Vorgänger',
  ready: 'Startbereit',
  dwell: 'Zeit in der Spalte',
  inactive: 'Ohne Bearbeitung',
  actor: 'Auslösende Person',
};

const PRIO = ['keine', 'niedrig', 'mittel', 'hoch', 'dringend'];

/* ---------- Felder ---------- */

export type FieldTestKind = 'text' | 'number' | 'select' | 'multiselect' | 'checkbox' | 'date' | 'person' | 'approval';
export function fieldTestKind(t: FieldType): FieldTestKind {
  if (t === 'number' || t === 'currency' || t === 'rating' || t === 'formula') return 'number';
  if (t === 'select' || t === 'multiselect' || t === 'checkbox' || t === 'date' || t === 'person' || t === 'approval') return t;
  return 'text';
}
export const FIELD_TEST_OPS: Record<FieldTestKind, FieldTestOp[]> = {
  text: ['filled', 'empty', 'is', 'isNot', 'contains', 'startsWith'],
  number: ['filled', 'empty', 'is', 'gt', 'gte', 'lt', 'lte', 'between'],
  select: ['filled', 'empty', 'anyOf', 'noneOf'],
  multiselect: ['filled', 'empty', 'anyOf', 'allOf', 'noneOf'],
  checkbox: ['checked', 'unchecked'],
  date: ['filled', 'empty', 'is', 'before', 'after', 'between'],
  person: ['filled', 'empty', 'is'],
  approval: ['approved', 'notApproved', 'rejected', 'empty'],
};
export const FIELD_TEST_LABEL: Record<FieldTestOp, string> = {
  empty: 'ist leer',
  filled: 'ist ausgefüllt',
  is: 'ist gleich',
  isNot: 'ist nicht',
  contains: 'enthält',
  startsWith: 'beginnt mit',
  gt: 'größer als',
  gte: 'mindestens',
  lt: 'kleiner als',
  lte: 'höchstens',
  between: 'zwischen',
  anyOf: 'ist eine von',
  allOf: 'enthält alle',
  noneOf: 'ist keine von',
  before: 'vor',
  after: 'nach',
  checked: 'ist angehakt',
  unchecked: 'ist nicht angehakt',
  approved: 'ist freigegeben',
  notApproved: 'ist nicht (vollständig) freigegeben',
  rejected: 'ist abgelehnt',
};
/** „ist eine von“ heißt bei Mehrfachauswahl „enthält eine von“ */
export function fieldTestLabel(kind: FieldTestKind, op: FieldTestOp): string {
  if (kind === 'approval' && op === 'empty') return 'ist noch offen (keine Entscheidung)';
  if (kind === 'multiselect' && op === 'anyOf') return 'enthält eine von';
  if (kind === 'multiselect' && op === 'noneOf') return 'enthält keine von';
  return FIELD_TEST_LABEL[op];
}

type FieldInfo = Pick<CustomField, 'id' | 'name' | 'type' | 'options'>;

const isEmpty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * Typgerechter Vergleich: Fehlend ist nicht 0, false ist kein leerer Zahlenwert, Text ohne
 * Groß-/Kleinschreibung, Datumsfelder als Kalenderdaten (JJJJ-MM-TT).
 */
export function fieldTestMatches(field: FieldInfo, value: unknown, t: FieldTest): boolean {
  const kind = fieldTestKind(field.type);
  if (kind === 'approval') {
    const st = approvalStatus(value);
    return t.op === 'approved' ? st === 'approved' : t.op === 'notApproved' ? st !== 'approved' : t.op === 'rejected' ? st === 'rejected' : t.op === 'empty' ? st === 'open' : t.op === 'filled' ? st !== 'open' : false;
  }
  if (t.op === 'empty') return kind === 'checkbox' ? !value : isEmpty(value);
  if (t.op === 'filled') return kind === 'checkbox' ? !!value : !isEmpty(value);
  if (kind === 'checkbox') return t.op === 'checked' ? value === true : t.op === 'unchecked' ? value !== true : false;
  if (kind === 'select' || kind === 'multiselect') {
    const have = Array.isArray(value) ? value.map(String) : isEmpty(value) ? [] : [String(value)];
    const want = t.optionIds ?? [];
    if (t.op === 'anyOf') return want.some((o) => have.includes(o));
    if (t.op === 'allOf') return want.length > 0 && want.every((o) => have.includes(o));
    if (t.op === 'noneOf') return !want.some((o) => have.includes(o));
    return false;
  }
  if (isEmpty(value)) return t.op === 'isNot';
  if (kind === 'number') {
    const a = Number(value);
    const b = Number(t.value);
    if (!Number.isFinite(a) || t.value === null || t.value === undefined || t.value === '' || !Number.isFinite(b)) return false;
    if (t.op === 'is') return a === b;
    if (t.op === 'gt') return a > b;
    if (t.op === 'gte') return a >= b;
    if (t.op === 'lt') return a < b;
    if (t.op === 'lte') return a <= b;
    if (t.op === 'between') {
      const c = Number(t.value2);
      return Number.isFinite(c) && a >= Math.min(b, c) && a <= Math.max(b, c);
    }
    return false;
  }
  if (kind === 'date') {
    const a = String(value).slice(0, 10);
    const b = String(t.value ?? '').slice(0, 10);
    if (!b) return false;
    if (t.op === 'is') return a === b;
    if (t.op === 'before') return a < b;
    if (t.op === 'after') return a > b;
    if (t.op === 'between') {
      const c = String(t.value2 ?? '').slice(0, 10);
      return !!c && a >= (b < c ? b : c) && a <= (b < c ? c : b);
    }
    return false;
  }
  if (kind === 'person') return t.op === 'is' && String(value) === String(t.value ?? '');
  const a = String(value).trim().toLowerCase();
  const b = String(t.value ?? '')
    .trim()
    .toLowerCase();
  if (t.op === 'is') return a === b;
  if (t.op === 'isNot') return a !== b;
  if (t.op === 'contains') return !!b && a.includes(b);
  if (t.op === 'startsWith') return !!b && a.startsWith(b);
  return false;
}

/* ---------- Kartenstand und Auswertung ---------- */

export interface AutomationFile {
  kind: 'image' | 'pdf' | 'other';
  /** gehört zu einem (veröffentlichten) Kommentar */
  comment: boolean;
}
export interface AutomationCardState {
  columnId: string;
  done: boolean;
  priority: number;
  labelIds: string[];
  assigneeIds: string[];
  blocked: boolean;
  custom: Record<string, unknown>;
  files: AutomationFile[];
  /** 0.60 */
  hasStart?: boolean;
  hasDue?: boolean;
  checklistTotal?: number;
  checklistDone?: number;
  /** 0.61: Termine wie gespeichert (ganztägig = Kalenderdatum), Aufwand, Auswertungszeitpunkt */
  startAt?: string | null;
  startHasTime?: boolean;
  dueAt?: string | null;
  dueHasTime?: boolean;
  estimateMinutes?: number | null;
  /** gebuchte Minuten (gespeicherte Einträge) */
  spentMinutes?: number;
  /** Beginn laufender Timer (ISO) */
  running?: string[];
  /** Auswertungszeitpunkt (ms); Standard jetzt */
  now?: number;
  timeZone?: string;
  /** 0.62: Startbereitschaft (wie Übergaben), Vorgänger, letzter Spalteneintritt, letzte Bearbeitung durch eine Person */
  ready?: boolean;
  predecessorsTotal?: number;
  predecessorsDone?: number;
  columnEnteredAt?: string | null;
  lastActivityAt?: string | null;
  /** 0.79 */
  people?: AutomationPeople;
}

export const fileKindOf = (mime: string): AutomationFile['kind'] => (mime === 'application/pdf' ? 'pdf' : mime.startsWith('image/') ? 'image' : 'other');
const fileMatches = (f: AutomationFile, kind: FileKind, includeComments: boolean) => (includeComments || !f.comment) && (kind === 'any' || f.kind === kind);

export const isGroup = (x: AutomationPredicate | ConditionGroup): x is ConditionGroup => 'items' in x;

/** Bedingungen einer Regel als Gruppe (Version 1: alle Bedingungen der Liste) */
export function ruleWhen(a: { when?: ConditionGroup | null; conditions?: AutomationCondition[] | null }): ConditionGroup {
  return a.when ?? { op: 'all', items: a.conditions ?? [] };
}

export function predicateMatches(p: AutomationPredicate, s: AutomationCardState, fields: Map<string, FieldInfo>): boolean {
  switch (p.type) {
    case 'hasLabel':
      return s.labelIds.includes(p.labelId);
    case 'notLabel':
      return !s.labelIds.includes(p.labelId);
    case 'priorityAtLeast':
      return s.priority >= p.value;
    case 'labels':
      return p.mode === 'any'
        ? p.labelIds.some((l) => s.labelIds.includes(l))
        : p.mode === 'all'
          ? p.labelIds.every((l) => s.labelIds.includes(l))
          : !p.labelIds.some((l) => s.labelIds.includes(l));
    case 'priority':
      return p.op === 'gte' ? s.priority >= p.value : p.op === 'lte' ? s.priority <= p.value : s.priority === p.value;
    case 'column':
      return p.mode === 'in' ? p.columnIds.includes(s.columnId) : !p.columnIds.includes(s.columnId);
    case 'done':
      return s.done === p.value;
    case 'files': {
      const n = s.files.filter((f) => fileMatches(f, p.kind, p.includeComments)).length;
      return p.min <= 0 ? n === 0 : n >= p.min;
    }
    case 'field': {
      const f = fields.get(p.fieldId);
      return !!f && fieldTestMatches(f, s.custom[p.fieldId], p.test);
    }
    case 'assignees':
      return p.mode === 'none' ? s.assigneeIds.length === 0 : p.mode === 'some' ? s.assigneeIds.length > 0 : !!p.userId && s.assigneeIds.includes(p.userId);
    case 'blocked':
      return s.blocked === p.value;
    case 'dates': {
      const has = p.which === 'start' ? !!s.hasStart : !!s.hasDue;
      return p.op === 'set' ? has : !has;
    }
    case 'checklist': {
      const total = s.checklistTotal ?? 0;
      const done = s.checklistDone ?? 0;
      // „Vollständig“ heißt: mindestens ein Punkt und alle erledigt; „Keine Checkliste“ ist ein eigener Zustand
      return p.op === 'none' ? total === 0 : p.op === 'complete' ? total > 0 && done === total : total > 0 && done < total;
    }
    case 'dateWindow':
    case 'effort':
    case 'estimate':
    case 'dwell':
    case 'inactive':
      return timePredicateMatches(p, s);
    case 'predecessors': {
      const total = s.predecessorsTotal ?? 0;
      const done = s.predecessorsDone ?? 0;
      // Ohne Vorgänger ist nichts „alle erledigt“ – keine leere Mengenprüfung
      return p.op === 'none' ? total === 0 : p.op === 'allDone' ? total > 0 && done === total : done < total;
    }
    case 'ready':
      return !!s.ready === p.value;
    case 'actor':
      return actorMatchesState(p, s);
  }
}

/**
 * 0.79: Ohne bekannte auslösende Person (Zeitpunkt, Formular, Vorschau) ist eine Personenbedingung nie
 * erfüllt – auch „ist nicht“. So öffnet eine Regel ohne Person nie eine Vier-Augen-Schranke.
 */
function actorMatches(p: Extract<AutomationPredicate, { type: 'actor' }>, people: AutomationPeople | undefined): boolean {
  if (!people?.actor) return false;
  const a = people.actor;
  const w = p.who;
  const hit =
    w.kind === 'creator'
      ? people.creator === a
      : w.kind === 'owner'
        ? people.owner === a
        : w.kind === 'assignee'
          ? false
          : w.kind === 'boardOwner'
            ? people.boardOwners.includes(a)
            : w.kind === 'approvedBy'
              ? false
              : w.kind === 'movedTo'
              ? people.movedBy[w.columnId] === a
              : w.kind === 'fieldSetBy'
                ? people.fieldSetBy[w.fieldId] === a
                : w.kind === 'user'
                  ? w.userId === a
                  : people.actorGroups.includes(w.groupId);
  return p.mode === 'is' ? hit : !hit;
}

/** Zuständigkeit braucht die Liste der Karte – darum eigene Auswertung mit dem Kartenstand */
function actorMatchesState(p: Extract<AutomationPredicate, { type: 'actor' }>, s: AutomationCardState): boolean {
  const w = p.who;
  if (w.kind !== 'assignee' && w.kind !== 'approvedBy') return actorMatches(p, s.people);
  const a = s.people?.actor;
  if (!a) return false;
  const hit = w.kind === 'assignee' ? s.assigneeIds.includes(a) : !!approvalOf(s.custom[w.fieldId])?.by.some((b) => b.userId === a);
  return p.mode === 'is' ? hit : !hit;
}

/** Braucht eine Bedingungsgruppe Personenangaben (`people`)? */
export function usesActor(g: ConditionGroup | null | undefined): boolean {
  return !!g && g.items.some((x) => (isGroup(x) ? usesActor(x) : x.type === 'actor'));
}

export interface PredicateResult {
  predicate: AutomationPredicate;
  ok: boolean;
  /** Tiefe 0 = oberste Gruppe */
  depth: number;
}

/** Gruppe auswerten; `out` sammelt jedes Prädikat mit Ergebnis (für die Vorschau) */
export function evaluateGroup(g: ConditionGroup, s: AutomationCardState, fields: Map<string, FieldInfo>, out?: PredicateResult[], depth = 0): boolean {
  if (!g.items.length) return true;
  const results = g.items.map((x) => {
    if (isGroup(x)) return evaluateGroup(x, s, fields, out, depth + 1);
    const ok = predicateMatches(x, s, fields);
    out?.push({ predicate: x, ok, depth });
    return ok;
  });
  return g.op === 'all' ? results.every(Boolean) : results.some(Boolean);
}

/* ---------- 0.79: Verbindliche Spaltenregeln ---------- */

export interface GateResult {
  ok: boolean;
  /** offene Bedingungen als Sätze („die auslösende Person nicht … ist“) */
  unmet: string[];
}

/** Was an einer verbindlichen Spaltenregel noch fehlt (leere Liste = erfüllt) */
export function checkGate(gate: { when: ConditionGroup }, s: AutomationCardState, fields: Map<string, FieldInfo>, ctx: DescribeContext): GateResult {
  const g = gate.when;
  if (evaluateGroup(g, s, fields)) return { ok: true, unmet: [] };
  if (g.op === 'any') return { ok: false, unmet: [describeGroup(g, ctx)] };
  const unmet = g.items
    .filter((x) => !(isGroup(x) ? evaluateGroup(x, s, fields) : predicateMatches(x, s, fields)))
    .map((x) => (isGroup(x) ? describeGroup(x, ctx, true) : describeCondition(x, ctx)));
  return { ok: false, unmet };
}

/**
 * 0.89 (K4): Unerfüllte Teile einer Spaltenregel mit dem Ort, an dem man sie erfüllt. Gleiche Auswertung wie
 * `checkGate` (dieselben Texte); Gruppen und personenabhängige Bedingungen haben kein Sprungziel.
 */
export function gateUnmetItems(gate: { when: ConditionGroup }, s: AutomationCardState, fields: Map<string, FieldInfo>, ctx: DescribeContext): HintItem[] {
  const g = gate.when;
  if (evaluateGroup(g, s, fields)) return [];
  if (g.op === 'any') return [{ text: describeGroup(g, ctx), target: null }];
  return g.items
    .filter((x) => !(isGroup(x) ? evaluateGroup(x, s, fields) : predicateMatches(x, s, fields)))
    .map((x) => (isGroup(x) ? { text: describeGroup(x, ctx, true), target: null } : { text: describeCondition(x, ctx), target: predicateTarget(x) }));
}

/** Bearbeitungsort einer Bedingung (null = nichts direkt einzutragen, z. B. Spalte, Person, Zeitablauf) */
export function predicateTarget(p: AutomationPredicate): HintTarget | null {
  switch (p.type) {
    case 'field':
      return { kind: 'field', fieldId: p.fieldId };
    case 'dates':
      return { kind: p.which === 'start' ? 'start' : 'due' };
    case 'checklist':
      return { kind: 'checklist' };
    case 'files':
      return { kind: 'files' };
    case 'assignees':
      return { kind: 'assignees' };
    case 'labels':
    case 'hasLabel':
    case 'notLabel':
      return { kind: 'labels' };
    case 'priority':
    case 'priorityAtLeast':
      return { kind: 'priority' };
    case 'estimate':
      return { kind: 'estimate' };
    case 'predecessors':
    case 'ready':
      return { kind: 'deps' };
    case 'blocked':
      return { kind: 'block' };
    case 'done':
      return { kind: 'done' };
    default:
      return null;
  }
}

/** Meldung für Menschen: eigener Hinweis oder „Nur wenn …“ */
export function gateMessage(gate: { message?: string | null }, side: 'entry' | 'exit', columnTitle: string, unmet: string[]): string {
  const head = side === 'entry' ? `„${columnTitle}“ nimmt die Karte noch nicht an` : `Die Karte darf „${columnTitle}“ noch nicht verlassen`;
  if (gate.message) return `${head}: ${gate.message}`;
  return `${head} – erst, wenn ${unmet.join(' und ')}.`;
}

/** Verweise einer Spaltenregel prüfen (wie Regeln: gelöschte Spalten/Felder/Labels machen sie reparaturbedürftig) */
export function gateProblems(gate: { when: ConditionGroup }, r: RuleRefs): string[] {
  return ruleProblems({ trigger: { type: 'manual', label: 'x' }, when: gate.when, actions: [] }, r).filter((p) => !p.startsWith('„Sobald'));
}

/* ---------- Ereignisse (aus dem Vorher/Nachher-Abgleich bzw. den bisherigen Auslösern) ---------- */

export type FiredEvent =
  | { type: 'moved'; toColumnId: string }
  | { type: 'created'; columnId: string }
  | { type: 'completed' }
  | { type: 'checklistDone' }
  | { type: 'overdue'; key: string }
  | { type: 'left'; fromColumnId: string }
  | { type: 'reopened' }
  | { type: 'labelAdded'; labelIds: string[] }
  | { type: 'labelRemoved'; labelIds: string[] }
  | { type: 'fieldChanged'; fieldId: string; from: unknown; to: unknown }
  | { type: 'fileAdded'; files: AutomationFile[] }
  | { type: 'fileRemoved'; files: AutomationFile[] }
  // 0.61
  | { type: 'dateChanged'; which: DateWhich; change: 'set' | 'changed' | 'removed' }
  | { type: 'dateReached'; which: DateWhich }
  | { type: 'effortChanged'; what: 'estimate' | 'spent' }
  // 0.62
  | { type: 'predecessorsDone' }
  | { type: 'ready' };

export function triggerMatches(t: AutomationTrigger, e: FiredEvent, fields: Map<string, FieldInfo>): boolean {
  if (t.type !== e.type) return false;
  switch (t.type) {
    case 'moved':
      return t.columnId === (e as { toColumnId: string }).toColumnId;
    case 'created':
      return !t.columnId || t.columnId === (e as { columnId: string }).columnId;
    case 'left':
      return t.columnId === (e as { fromColumnId: string }).fromColumnId;
    case 'labelAdded':
    case 'labelRemoved': {
      const ids = (e as { labelIds: string[] }).labelIds;
      return t.labelIds.length === 0 ? ids.length > 0 : ids.some((l) => t.labelIds.includes(l));
    }
    case 'fieldChanged': {
      const ev = e as { fieldId: string; from: unknown; to: unknown };
      if (ev.fieldId !== t.fieldId) return false;
      const f = fields.get(t.fieldId);
      if (!f) return false;
      if (t.from && !fieldTestMatches(f, ev.from, t.from)) return false;
      if (t.to && !fieldTestMatches(f, ev.to, t.to)) return false;
      return true;
    }
    case 'fileAdded':
    case 'fileRemoved':
      return (e as { files: AutomationFile[] }).files.some((f) => fileMatches(f, t.kind, t.includeComments));
    case 'dateChanged': {
      const ev = e as { which: DateWhich; change: string };
      return ev.which === t.which && (t.change === 'any' || t.change === ev.change);
    }
    case 'dateReached':
      return (e as { which: DateWhich }).which === t.which;
    case 'effortChanged':
      return (e as { what: string }).what === t.what;
    default:
      return true;
  }
}

/* ---------- Verweise prüfen (Speichern, Liste, Ausführung) ---------- */

export interface RuleRefs {
  columns: Set<string>;
  labels: Set<string>;
  members: Set<string>;
  fields: Map<string, FieldInfo>;
  /** 0.63: Teamgruppen des Boards (Empfänger) */
  groups?: Set<string>;
}

function countPredicates(g: ConditionGroup): number {
  return g.items.reduce((n, x) => n + (isGroup(x) ? countPredicates(x) : 1), 0);
}

/**
 * Was an einer Regel nicht (mehr) stimmt – leere Liste = in Ordnung. Nur über IDs: Umbenennen bleibt
 * folgenlos; gelöschte Spalten, Labels, Felder, Optionen oder ein geänderter Feldtyp machen die Regel
 * reparaturbedürftig. Nie nach ähnlichem Namen neu zuordnen.
 */
export function ruleProblems(a: { trigger: AutomationTrigger; when: ConditionGroup; actions: AutomationAction[] }, r: RuleRefs): string[] {
  const out: string[] = [];
  const col = (id: string, where: string) => !r.columns.has(id) && out.push(`${where}: Die Spalte gibt es nicht mehr.`);
  const lab = (id: string, where: string) => !r.labels.has(id) && out.push(`${where}: Ein Label gibt es nicht mehr.`);
  const fieldOk = (id: string, where: string, kinds?: FieldTestKind[]): FieldInfo | null => {
    const f = r.fields.get(id);
    if (!f) {
      out.push(`${where}: Das eigene Feld gibt es nicht mehr.`);
      return null;
    }
    if (kinds && !kinds.includes(fieldTestKind(f.type))) {
      out.push(`${where}: „${f.name}“ hat inzwischen einen anderen Typ.`);
      return null;
    }
    return f;
  };
  const testOk = (f: FieldInfo, t: FieldTest, where: string) => {
    const kind = fieldTestKind(f.type);
    if (!FIELD_TEST_OPS[kind].includes(t.op)) out.push(`${where}: Dieser Vergleich passt nicht (mehr) zum Typ von „${f.name}“.`);
    for (const o of t.optionIds ?? []) if (!f.options.some((x) => x.id === o)) out.push(`${where}: Eine Auswahl von „${f.name}“ gibt es nicht mehr.`);
  };
  const t = a.trigger;
  if (t.type === 'moved' || t.type === 'left') col(t.columnId, 'Auslöser');
  if (t.type === 'created' && t.columnId) col(t.columnId, 'Auslöser');
  if (t.type === 'labelAdded' || t.type === 'labelRemoved') t.labelIds.forEach((l) => lab(l, 'Auslöser'));
  if (t.type === 'fieldChanged') {
    const f = fieldOk(t.fieldId, 'Auslöser');
    if (f) for (const x of [t.from, t.to]) if (x) testOk(f, x, 'Auslöser');
  }
  if (t.type === 'dateReached' && t.repeat && !(t.repeat.every > 0 && t.repeat.max >= 1)) out.push('Auslöser: Eine Wiederholung braucht Abstand und Höchstzahl.');
  if (t.type === 'dateReached' && t.fieldId) fieldOk(t.fieldId, 'Auslöser', ['date']);
  if (countPredicates(a.when) > AUTOMATION_MAX_PREDICATES) out.push(`Höchstens ${AUTOMATION_MAX_PREDICATES} Bedingungen.`);
  if (t.type === 'stateMet' && countPredicates(a.when) === 0) out.push('„Sobald erfüllt“ braucht mindestens eine Bedingung.');
  const walk = (g: ConditionGroup, depth: number) => {
    for (const x of g.items) {
      if (isGroup(x)) {
        if (depth >= 1) out.push('Gruppen lassen sich nur eine Ebene tief verschachteln.');
        else walk(x, depth + 1);
        continue;
      }
      const w = 'Bedingung';
      if (x.type === 'hasLabel' || x.type === 'notLabel') lab(x.labelId, w);
      if (x.type === 'labels') x.labelIds.forEach((l) => lab(l, w));
      if (x.type === 'column') x.columnIds.forEach((c) => col(c, w));
      if (x.type === 'assignees' && x.mode === 'includes' && (!x.userId || !r.members.has(x.userId))) out.push(`${w}: Die Person ist nicht mehr im Board.`);
      if (x.type === 'field') {
        const f = fieldOk(x.fieldId, w);
        if (f) testOk(f, x.test, w);
      }
      if (x.type === 'actor') actorProblems(x.who, r, w).forEach((m) => out.push(m));
    }
  };
  walk(a.when, 0);
  for (const x of a.actions) {
    const w = 'Aktion';
    if (x.type === 'move') col(x.columnId, w);
    if (x.type === 'addLabel' || x.type === 'removeLabel') lab(x.labelId, w);
    if (x.type === 'labels') [...x.add, ...x.remove].forEach((l) => lab(l, w));
    if (x.type === 'assign' && x.userId !== 'actor' && !r.members.has(x.userId)) out.push(`${w}: Die Person ist nicht mehr im Board.`);
    if (x.type === 'clearField') fieldOk(x.fieldId, w);
    if (x.type === 'notify' && x.recipients) {
      if (x.recipients.users.some((u) => !r.members.has(u))) out.push(`${w}: Eine Empfängerin oder ein Empfänger ist nicht mehr im Board.`);
      if (r.groups && x.recipients.groups.some((g) => !r.groups!.has(g))) out.push(`${w}: Eine Gruppe gibt es nicht mehr (oder das Board gehört nicht zu ihrem Team).`);
    }
    if (x.type === 'setField' && ['sequence', 'formula'].includes(r.fields.get(x.fieldId)?.type ?? '')) out.push(`${w}: Dieses Feld rechnet bzw. vergibt KaBa selbst.`);
    if (x.type === 'calcField' && r.fields.get(x.fieldId)?.type === 'formula') out.push(`${w}: Ein berechnetes Feld lässt sich nicht verändern.`);
    if (x.type === 'setField' && r.fields.get(x.fieldId)?.type === 'approval') out.push(`${w}: Eine Freigabe lässt sich nicht von einer Regel setzen – nur leeren (zurücksetzen).`);
    if (x.type === 'setField') {
      const f = fieldOk(x.fieldId, w);
      const ids = f && (f.type === 'select' || f.type === 'multiselect') ? (Array.isArray(x.value) ? x.value : [x.value]).map(String) : [];
      if (f && ids.some((o) => !f.options.some((y) => y.id === o))) out.push(`${w}: Eine Auswahl von „${f.name}“ gibt es nicht mehr.`);
    }
    if (x.type === 'fieldOptions') {
      const f = fieldOk(x.fieldId, w, ['multiselect']);
      if (f) for (const o of [...x.add, ...x.remove]) if (!f.options.some((y) => y.id === o)) out.push(`${w}: Eine Auswahl von „${f.name}“ gibt es nicht mehr.`);
    }
    if (x.type === 'setDate') {
      if (x.which === 'field') fieldOk(x.fieldId ?? '', w, ['date']);
      if (x.base === 'field') fieldOk(x.baseFieldId ?? '', w, ['date']);
      if (x.base === 'fixed' && !/^\d{4}-\d{2}-\d{2}$/.test(x.baseDate ?? '')) out.push(`${w}: Wähle ein festes Datum.`);
      if (x.which === x.base && x.amount === 0) out.push(`${w}: Der Termin würde auf sich selbst gesetzt.`);
    }
    if (x.type === 'calcField') {
      fieldOk(x.fieldId, w, ['number']);
      if (x.fromFieldId) fieldOk(x.fromFieldId, w, ['number']);
      else if (x.value === null || !Number.isFinite(x.value)) out.push(`${w}: Gib eine Zahl an oder wähle ein Feld.`);
      if (r.fields.get(x.fieldId)?.type === 'rating') out.push(`${w}: Mit einer Bewertung lässt sich nicht rechnen.`);
    }
    if (x.type === 'createCard') {
      if (!x.boardId) col(x.columnId, w);
      if (!x.title.trim()) out.push(`${w}: Die Folgekarte braucht einen Titel.`);
    }
    if ((x.type === 'setDate' || x.type === 'shiftDate') && Math.abs(x.amount) > AMOUNT_MAX[x.unit]) out.push(`${w}: Der Abstand ist zu groß (höchstens ${unitText(AMOUNT_MAX[x.unit], x.unit)}).`);
  }
  return [...new Set(out)];
}

/** 0.79: Verweise einer Personenbedingung (Spalte, Feld, Person, Gruppe) */
export function actorProblems(w: ActorRef, r: RuleRefs, where: string): string[] {
  if (w.kind === 'movedTo' && !r.columns.has(w.columnId)) return [`${where}: Die Spalte gibt es nicht mehr.`];
  if (w.kind === 'fieldSetBy' && !r.fields.has(w.fieldId)) return [`${where}: Das eigene Feld gibt es nicht mehr.`];
  if (w.kind === 'approvedBy' && r.fields.get(w.fieldId)?.type !== 'approval') return [`${where}: Das Freigabefeld gibt es nicht mehr.`];
  if (w.kind === 'user' && !r.members.has(w.userId)) return [`${where}: Die Person ist nicht mehr im Board.`];
  if (w.kind === 'group' && r.groups && !r.groups.has(w.groupId)) return [`${where}: Eine Gruppe gibt es nicht mehr (oder das Board gehört nicht zu ihrem Team).`];
  return [];
}

/* ---------- Beschreibung ---------- */

export interface DescribeContext {
  column: (id: string) => string | undefined;
  label: (id: string) => string | undefined;
  person: (id: string) => string | undefined;
  /** 0.59: eigene Felder (für Namen, Optionen und Typ) */
  field?: (id: string) => FieldInfo | undefined;
  /** 0.63: Teamgruppen */
  group?: (id: string) => string | undefined;
}

const q = (s: string | undefined) => `„${s ?? '?'}“`;
const list = (xs: string[], word = 'oder') => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} ${word} ${xs.at(-1)}`);
const FILE_KIND: Record<FileKind, string> = { any: 'Datei', image: 'Bild', pdf: 'PDF-Datei', other: 'sonstige Datei' };
const FILE_KIND_PL: Record<FileKind, string> = { any: 'Dateien', image: 'Bilder', pdf: 'PDF-Dateien', other: 'sonstige Dateien' };

function dateDe(v: unknown): string {
  const s = String(v ?? '');
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s;
}

export function describeFieldTest(f: FieldInfo | undefined, t: FieldTest, ctx?: DescribeContext): string {
  if (!f) return `${FIELD_TEST_LABEL[t.op]}`;
  const kind = fieldTestKind(f.type);
  const op = fieldTestLabel(kind, t.op);
  const opt = (ids?: string[]) =>
    list(
      (ids ?? []).map((id) => q(f.options.find((o) => o.id === id)?.label)),
      t.op === 'allOf' ? 'und' : 'oder',
    );
  const val = (v: unknown) => (kind === 'date' ? dateDe(v) : kind === 'person' ? (ctx?.person(String(v)) ?? 'Person') : `„${String(v ?? '')}“`);
  switch (t.op) {
    case 'empty':
    case 'filled':
    case 'checked':
    case 'unchecked':
    case 'approved':
    case 'notApproved':
    case 'rejected':
      return op;
    case 'anyOf':
    case 'allOf':
    case 'noneOf':
      return `${op} ${opt(t.optionIds)}`;
    case 'between':
      return `${op} ${val(t.value)} und ${val(t.value2)}`;
    default:
      return `${op} ${val(t.value)}`;
  }
}

export function describeTrigger(t: AutomationTrigger, ctx: DescribeContext): string {
  switch (t.type) {
    case 'moved':
      return `eine Karte in ${q(ctx.column(t.columnId))} kommt`;
    case 'created':
      return t.columnId ? `eine Karte in ${q(ctx.column(t.columnId))} angelegt wird` : 'eine Karte angelegt wird';
    case 'left':
      return `eine Karte ${q(ctx.column(t.columnId))} verlässt`;
    case 'labelAdded':
    case 'labelRemoved': {
      const verb = t.type === 'labelAdded' ? 'hinzugefügt' : 'entfernt';
      if (!t.labelIds.length) return `ein Label ${verb} wird`;
      return t.labelIds.length === 1
        ? `das Label ${q(ctx.label(t.labelIds[0]!))} ${verb} wird`
        : `eines der Labels ${list(t.labelIds.map((l) => q(ctx.label(l))))} ${verb} wird`;
    }
    case 'fieldChanged': {
      const f = ctx.field?.(t.fieldId);
      const from = t.from ? ` von „${describeFieldTest(f, t.from, ctx)}“` : '';
      const to = t.to ? ` auf „${describeFieldTest(f, t.to, ctx)}“` : '';
      return `${q(f?.name)} sich${from}${to} ändert`;
    }
    case 'dateChanged': {
      const n = DATE_NAME[t.which].nom;
      const verb = t.change === 'set' ? 'gesetzt' : t.change === 'changed' ? 'geändert' : t.change === 'removed' ? 'entfernt' : 'gesetzt, geändert oder entfernt';
      return `${n} ${verb} wird`;
    }
    case 'dateReached': {
      const r = t.repeat;
      const every = !r ? '' : r.every === 1 ? (r.unit === 'hours' ? 'stündlich' : 'täglich') : `alle ${unitText(r.every, r.unit)}`;
      const rep = r ? ` (danach ${every}, höchstens ${r.max}-mal)` : '';
      return `${dateOffsetText(t.which, t.offset, t.fieldId ? (ctx.field?.(t.fieldId)?.name ?? 'Datumsfeld') : undefined)}${rep}`;
    }
    case 'effortChanged':
      return t.what === 'estimate' ? 'die Schätzung geändert wird' : 'Zeit gebucht oder korrigiert wird';
    case 'manual':
      return `jemand an der Karte ${q(t.label)} wählt`;
    case 'fileAdded':
    case 'fileRemoved': {
      const what = `${t.kind === 'any' ? 'eine Datei' : t.kind === 'image' ? 'ein Bild' : t.kind === 'pdf' ? 'eine PDF-Datei' : 'eine sonstige Datei'}${t.includeComments ? ' (auch aus Kommentaren)' : ''}`;
      return t.type === 'fileAdded' ? `${what} an der Karte verfügbar wird` : `${what} von der Karte entfernt wird`;
    }
    default:
      return TRIGGER_LABEL[t.type];
  }
}

export function describeCondition(c: AutomationPredicate, ctx: DescribeContext): string {
  switch (c.type) {
    case 'hasLabel':
      return `sie das Label ${q(ctx.label(c.labelId))} hat`;
    case 'notLabel':
      return `sie das Label ${q(ctx.label(c.labelId))} nicht hat`;
    case 'priorityAtLeast':
      return `die Priorität mindestens „${PRIO[c.value]}“ ist`;
    case 'labels': {
      const names = c.labelIds.map((l) => q(ctx.label(l)));
      if (c.labelIds.length === 1) return c.mode === 'none' ? `sie das Label ${names[0]} nicht hat` : `sie das Label ${names[0]} hat`;
      return c.mode === 'any'
        ? `sie eines der Labels ${list(names)} hat`
        : c.mode === 'all'
          ? `sie die Labels ${list(names, 'und')} hat`
          : `sie keines der Labels ${list(names)} hat`;
    }
    case 'priority':
      return `die Priorität ${c.op === 'gte' ? 'mindestens' : c.op === 'lte' ? 'höchstens' : ''} „${PRIO[c.value]}“ ist`.replace('  ', ' ');
    case 'column': {
      const names = list(c.columnIds.map((x) => q(ctx.column(x))));
      return c.mode === 'in' ? `sie in ${names} liegt` : `sie nicht in ${names} liegt`;
    }
    case 'done':
      return c.value ? 'sie erledigt ist' : 'sie offen ist';
    case 'files':
      return c.min <= 0
        ? `sie keine ${FILE_KIND_PL[c.kind]}${c.includeComments ? ' (auch aus Kommentaren)' : ''} hat`
        : `sie mindestens ${c.min === 1 ? `eine ${FILE_KIND[c.kind]}`.replace('eine Bild', 'ein Bild') : `${c.min} ${FILE_KIND_PL[c.kind]}`}${c.includeComments ? ' (auch aus Kommentaren)' : ''} hat`;
    case 'field': {
      const f = ctx.field?.(c.fieldId);
      return `${q(f?.name)} ${describeFieldTest(f, c.test, ctx)}`;
    }
    case 'assignees':
      return c.mode === 'none' ? 'niemand zuständig ist' : c.mode === 'some' ? 'jemand zuständig ist' : `${ctx.person(c.userId ?? '') ?? 'Person'} zuständig ist`;
    case 'blocked':
      return c.value ? 'sie blockiert ist' : 'sie nicht blockiert ist';
    case 'dates':
      return `${c.which === 'start' ? 'der Start' : 'die Frist'} ${c.op === 'set' ? 'gesetzt ist' : 'fehlt'}`;
    case 'checklist':
      return c.op === 'none' ? 'sie keine Checkliste hat' : c.op === 'complete' ? 'die Checkliste vollständig ist' : 'die Checkliste noch offene Punkte hat';
    case 'dateWindow': {
      const n = DATE_NAME[c.which].nom;
      const m = minutesText(c.minutes ?? 0);
      if (c.op === 'reached') return `${n} erreicht ist`;
      if (c.op === 'notReached') return `${n} noch nicht erreicht ist`;
      if (c.op === 'within') return `${n} in höchstens ${m} erreicht ist`;
      return c.which === 'due' ? `die Frist seit mindestens ${m} überschritten ist` : `der Start seit mindestens ${m} erreicht ist`;
    }
    case 'effort': {
      const running = c.includeRunning ? ' (mit laufenden Timern)' : '';
      if (c.measure === 'percent') return `die gebuchte Zeit${running} ${c.op === 'gte' ? 'mindestens' : 'unter'} ${c.value} % der Schätzung ${c.op === 'gte' ? 'erreicht' : 'liegt'}`;
      return `${c.op === 'gte' ? 'mindestens' : 'weniger als'} ${minutesText(c.value)} gebucht ${c.value === 1 ? 'ist' : 'sind'}${running}`;
    }
    case 'estimate':
      return c.op === 'set' ? 'eine Schätzung vorhanden ist' : 'keine Schätzung vorhanden ist';
    case 'predecessors':
      return c.op === 'none' ? 'sie keine Vorgänger hat' : c.op === 'allDone' ? 'alle Vorgänger erledigt sind' : 'noch Vorgänger offen sind';
    case 'ready':
      return c.value ? 'sie startbereit ist' : 'sie nicht startbereit ist';
    case 'dwell':
      return `sie seit ${c.op === 'atLeast' ? 'mindestens' : 'weniger als'} ${minutesDat(c.minutes)} in ihrer Spalte liegt`;
    case 'inactive':
      return c.op === 'atLeast' ? `seit mindestens ${minutesDat(c.minutes)} niemand an ihr gearbeitet hat` : `ihre letzte Bearbeitung weniger als ${minutesText(c.minutes)} zurückliegt`;
    case 'actor':
      return `die auslösende Person ${describeActorRef(c.who, c.mode === 'isNot', ctx)}`;
  }
}

/** Satzrest hinter „die auslösende Person“ */
export function describeActorRef(w: ActorRef, not: boolean, ctx: DescribeContext): string {
  const n = not ? 'nicht ' : '';
  switch (w.kind) {
    case 'creator':
      return `die Karte ${n}angelegt hat`;
    case 'owner':
      return `${n}hauptverantwortlich ist`;
    case 'assignee':
      return `${n}zuständig ist`;
    case 'boardOwner':
      return `${n}Besitzer*in des Boards ist`;
    case 'movedTo':
      return `die Karte ${not ? 'nicht selbst' : 'selbst'} nach ${q(ctx.column(w.columnId))} gebracht hat`;
    case 'fieldSetBy':
      return `${q(ctx.field?.(w.fieldId)?.name)} ${not ? 'nicht selbst' : 'selbst'} gesetzt hat`;
    case 'approvedBy':
      return `bei ${q(ctx.field?.(w.fieldId)?.name)} ${not ? 'nicht ' : ''}freigegeben hat`;
    case 'user':
      return `${n}${ctx.person(w.userId) ?? 'die gewählte Person'} ist`;
    case 'group':
      return `${n}in der Gruppe ${q(ctx.group?.(w.groupId))} ist`;
  }
}

/** „3 Tagen“, „2 Stunden“ (Dativ) */
const minutesDat = (m: number) => minutesText(m).replace(/ Tage$/, ' Tagen').replace(/^1 Tag$/, 'einem Tag').replace(/^1 Stunde$/, 'einer Stunde');

export function describeGroup(g: ConditionGroup, ctx: DescribeContext, nested = false): string {
  const parts = g.items.map((x) => (isGroup(x) ? describeGroup(x, ctx, true) : describeCondition(x, ctx)));
  const text = parts.join(g.op === 'all' ? ' und ' : ' oder ');
  return nested && parts.length > 1 ? `(${text})` : text;
}

export function describeAction(a: AutomationAction, ctx: DescribeContext): string {
  switch (a.type) {
    case 'move':
      return `${a.placement === 'top' ? 'oben' : 'unten'} in ${q(ctx.column(a.columnId))} verschieben`;
    case 'assign':
      return a.userId === 'actor' ? 'die auslösende Person zuweisen' : `${ctx.person(a.userId) ?? 'Person'} zuweisen`;
    case 'addLabel':
      return `Label ${q(ctx.label(a.labelId))} hinzufügen`;
    case 'removeLabel':
      return `Label ${q(ctx.label(a.labelId))} entfernen`;
    case 'labels': {
      const add = a.add.length ? `${a.add.length === 1 ? 'Label' : 'Labels'} ${list(a.add.map((l) => q(ctx.label(l))), 'und')} hinzufügen` : '';
      const rem = a.remove.length ? `${a.remove.length === 1 ? 'Label' : 'Labels'} ${list(a.remove.map((l) => q(ctx.label(l))), 'und')} entfernen` : '';
      return [add, rem].filter(Boolean).join(' und ') || 'Labels unverändert lassen';
    }
    case 'setField': {
      const f = ctx.field?.(a.fieldId);
      const kind = f ? fieldTestKind(f.type) : 'text';
      const v =
        kind === 'select' || kind === 'multiselect'
          ? list((Array.isArray(a.value) ? a.value : [a.value]).map((id) => q(f?.options.find((o) => o.id === id)?.label)), 'und')
          : kind === 'checkbox'
            ? a.value
              ? 'angehakt'
              : 'nicht angehakt'
            : kind === 'date'
              ? dateDe(a.value)
              : kind === 'person'
                ? (ctx.person(String(a.value)) ?? 'Person')
                : `„${String(a.value)}“`;
      return `${q(f?.name)} auf ${v} setzen`;
    }
    case 'clearField':
      return `${q(ctx.field?.(a.fieldId)?.name)} leeren`;
    case 'fieldOptions': {
      const f = ctx.field?.(a.fieldId);
      const n = (ids: string[]) => list(ids.map((id) => q(f?.options.find((o) => o.id === id)?.label)), 'und');
      return [a.add.length ? `in ${q(f?.name)} ${n(a.add)} ergänzen` : '', a.remove.length ? `aus ${q(f?.name)} ${n(a.remove)} entfernen` : ''].filter(Boolean).join(' und ');
    }
    case 'setDate': {
      const target = a.which === 'field' ? q(ctx.field?.(a.fieldId ?? '')?.name) : a.which === 'start' ? 'Start' : 'Frist';
      const baseAcc =
        a.base === 'now'
          ? 'jetzt'
          : a.base === 'event'
            ? 'den Zeitpunkt des Auslösers'
            : a.base === 'start' || a.base === 'due'
              ? DATE_NAME[a.base].acc
              : a.base === 'field'
                ? q(ctx.field?.(a.baseFieldId ?? '')?.name)
                : `${dateDe(a.baseDate)}${a.baseTime ? ` ${a.baseTime} Uhr` : ''}`;
      const baseDat =
        a.base === 'now'
          ? null
          : a.base === 'event'
            ? 'dem Auslöser'
            : a.base === 'start' || a.base === 'due'
              ? DATE_NAME[a.base].dat
              : a.base === 'field'
                ? q(ctx.field?.(a.baseFieldId ?? '')?.name)
                : `dem ${dateDe(a.baseDate)}${a.baseTime ? ` ${a.baseTime} Uhr` : ''}`;
      const when =
        a.amount === 0
          ? baseAcc
          : baseDat
            ? `${unitText(a.amount, a.unit)} ${a.amount < 0 ? 'vor' : 'nach'} ${baseDat}`
            : `${a.amount < 0 ? 'vor' : 'in'} ${unitText(a.amount, a.unit).replace(/Kalendertage$/, 'Kalendertagen').replace(/Arbeitstage$/, 'Arbeitstagen')}`;
      const calendar = a.unit === 'days' || a.unit === 'weeks' || a.unit === 'workdays';
      const time = a.which === 'field' || !calendar ? '' : a.time === 'allDay' ? ', ganztägig' : a.time === 'base' ? '' : `, ${a.time} Uhr`;
      return `${target} auf ${when} setzen${time}${workdayNote(a)}${a.onlyIfEmpty ? ' (nur wenn leer)' : ''}`;
    }
    case 'shiftDate':
      return `${a.which === 'start' ? 'Start' : 'Frist'} um ${unitText(a.amount, a.unit)} ${a.amount < 0 ? 'vorziehen' : 'verschieben'}${workdayNote(a)}`;
    case 'clearDate':
      return a.which === 'start' ? 'Start entfernen' : 'Frist entfernen';
    case 'setEstimate':
      return a.minutes === null ? 'Schätzung leeren' : `Schätzung auf ${minutesText(a.minutes)} setzen`;
    case 'calcField': {
      const f = q(ctx.field?.(a.fieldId)?.name);
      const v = a.fromFieldId ? q(ctx.field?.(a.fromFieldId)?.name) : (a.value ?? 0).toLocaleString('de-DE');
      return a.op === 'add' ? `${v} zu ${f} addieren` : a.op === 'sub' ? `${v} von ${f} abziehen` : a.op === 'mul' ? `${f} mit ${v} malnehmen` : `${f} auf ${v} setzen`;
    }
    case 'createCard':
      return `Folgekarte „${a.title}“ ${a.boardId ? 'in einem anderen Board' : `in ${q(ctx.column(a.columnId))}`} anlegen`;
    case 'setDue':
      return a.days === 0 ? 'Frist auf heute setzen' : a.days === 1 ? 'Frist auf morgen setzen' : `Frist in ${a.days} Tagen setzen`;
    case 'setPriority':
      return `Priorität „${PRIO[a.value]}“ setzen`;
    case 'addChecklist':
      return `Checkliste ergänzen (${a.items.length} ${a.items.length === 1 ? 'Punkt' : 'Punkte'})`;
    case 'notify':
      return `${notifyRecipientsText(a.recipients, ctx)} benachrichtigen: „${a.text}“`;
    default:
      return ACTION_LABEL[a.type];
  }
}

/** „Zuständige, Hauptverantwortliche und Olga“ – Gruppen über `ctx.group` */
export function notifyRecipientsText(r: NotifyRecipients | undefined, ctx: DescribeContext): string {
  if (!r) return 'Zuständige';
  const parts = [
    ...(r.assignees ? ['Zuständige'] : []),
    ...(r.owner ? ['Hauptverantwortliche'] : []),
    ...(r.watchers ? ['Beobachtende'] : []),
    ...r.users.map((u) => ctx.person(u) ?? 'Person'),
    ...r.groups.map((g) => `Gruppe ${q(ctx.group?.(g) ?? 'Gruppe')}`),
  ];
  return parts.length ? list(parts, 'und') : 'niemanden';
}

export function describeAutomation(
  a: { trigger: AutomationTrigger; conditions?: AutomationCondition[]; when?: ConditionGroup | null; actions: AutomationAction[]; once?: boolean },
  ctx: DescribeContext,
): string {
  const g = ruleWhen(a);
  if (a.trigger.type === 'stateMet') {
    const cond = g.items.length ? describeGroup(g, ctx, false).replace(/^sie /, 'eine Karte ') : '…';
    const text = `Sobald ${cond}: ${a.actions.map((x) => describeAction(x, ctx)).join(', ')}${a.once ? ' (nur einmal je Karte)' : ''}`;
    return /[.!?]“$/.test(text) ? text : `${text}.`;
  }
  const cond = g.items.length ? ` und ${describeGroup(g, ctx, g.op === 'any' && g.items.length > 1)}` : '';
  const text = `Wenn ${describeTrigger(a.trigger, ctx)}${cond}: ${a.actions.map((x) => describeAction(x, ctx)).join(', ')}`;
  // Kein doppeltes Satzzeichen nach einem Zitat, das schon mit . ! ? endet
  return /[.!?]“$/.test(text) ? text : `${text}.`;
}

/* ---------- Verweise umschreiben (Vorlagen, Export/Import, Duplizieren) ---------- */

export interface RefMapper {
  column: (id: string) => string;
  label: (id: string) => string;
  field: (id: string) => string;
}

/** Alle Spalten-, Label- und Feld-IDs einer Regel abbilden (Optionen bleiben – sie gehören zum Feld) */
export function mapRuleRefs<T extends { trigger: AutomationTrigger; conditions?: AutomationCondition[]; when?: ConditionGroup | null; actions: AutomationAction[] }>(a: T, m: RefMapper): T {
  const t = a.trigger;
  const trigger: AutomationTrigger =
    t.type === 'moved' || t.type === 'left'
      ? { ...t, columnId: m.column(t.columnId) }
      : t.type === 'created'
        ? { ...t, columnId: t.columnId ? m.column(t.columnId) : null }
        : t.type === 'labelAdded' || t.type === 'labelRemoved'
          ? { ...t, labelIds: t.labelIds.map(m.label) }
          : t.type === 'dateReached' && t.fieldId
            ? { ...t, fieldId: m.field(t.fieldId) }
          : t.type === 'fieldChanged'
            ? { ...t, fieldId: m.field(t.fieldId) }
            : t;
  const pred = (p: AutomationPredicate): AutomationPredicate => {
    switch (p.type) {
      case 'hasLabel':
      case 'notLabel':
        return { ...p, labelId: m.label(p.labelId) };
      case 'labels':
        return { ...p, labelIds: p.labelIds.map(m.label) };
      case 'column':
        return { ...p, columnIds: p.columnIds.map(m.column) };
      case 'field':
        return { ...p, fieldId: m.field(p.fieldId) };
      case 'actor':
        return { ...p, who: mapActorRef(p.who, m) };
      default:
        return p;
    }
  };
  const group = (g: ConditionGroup): ConditionGroup => ({ op: g.op, items: g.items.map((x) => (isGroup(x) ? group(x) : pred(x))) });
  const actions = a.actions.map((x): AutomationAction => {
    switch (x.type) {
      case 'move':
        return { ...x, columnId: m.column(x.columnId) };
      case 'addLabel':
      case 'removeLabel':
        return { ...x, labelId: m.label(x.labelId) };
      case 'labels':
        return { ...x, add: x.add.map(m.label), remove: x.remove.map(m.label) };
      case 'setField':
      case 'clearField':
      case 'fieldOptions':
        return { ...x, fieldId: m.field(x.fieldId) };
      case 'setDate':
        return { ...x, ...(x.fieldId ? { fieldId: m.field(x.fieldId) } : {}), ...(x.baseFieldId ? { baseFieldId: m.field(x.baseFieldId) } : {}) };
      case 'calcField':
        return { ...x, fieldId: m.field(x.fieldId), ...(x.fromFieldId ? { fromFieldId: m.field(x.fromFieldId) } : {}) };
      case 'createCard':
        // Andere Boards bleiben (nur dieses Board wird abgebildet)
        return x.boardId ? x : { ...x, columnId: m.column(x.columnId) };
      default:
        return x;
    }
  });
  return {
    ...a,
    trigger,
    ...(a.conditions ? { conditions: a.conditions.map((c) => pred(c) as AutomationCondition) } : {}),
    ...(a.when ? { when: group(a.when) } : {}),
    actions,
  };
}

/** 0.79: Spalte/Feld einer Personenbedingung abbilden (Personen und Gruppen bleiben) */
export function mapActorRef(w: ActorRef, m: RefMapper): ActorRef {
  if (w.kind === 'movedTo') return { ...w, columnId: m.column(w.columnId) };
  if (w.kind === 'fieldSetBy' || w.kind === 'approvedBy') return { ...w, fieldId: m.field(w.fieldId) };
  return w;
}

/** Bezieht sich eine Gruppe auf feste Personen oder Gruppen (nicht übertragbar in Vorlagen/Export)? */
export function groupHasPersonRef(g: ConditionGroup | null | undefined): boolean {
  return !!g && g.items.some((x) => (isGroup(x) ? groupHasPersonRef(x) : (x.type === 'actor' && (x.who.kind === 'user' || x.who.kind === 'group')) || (x.type === 'assignees' && x.mode === 'includes')));
}

/** Alle Verweise einer Regel (zum Prüfen, ob sie in einer Vorlage auflösbar sind) */
export function ruleRefList(a: { trigger: AutomationTrigger; conditions?: AutomationCondition[]; when?: ConditionGroup | null; actions: AutomationAction[] }): string[] {
  const out: string[] = [];
  mapRuleRefs(a, {
    column: (id) => (out.push(id), id),
    label: (id) => (out.push(id), id),
    field: (id) => (out.push(id), id),
  });
  return out;
}

/** 0.59: gespeicherter Stand je Karte für den Vorher/Nachher-Abgleich (nur Server) */
export interface AutomationFactsSnapshot {
  v: 1;
  col: string;
  done: boolean;
  labels: string[];
  custom: Record<string, unknown>;
  /** Dateien: ID, Art, aus Kommentar */
  files: { id: string; k: AutomationFile['kind']; c: boolean }[];
  /** 0.61 (fehlt bei älteren Ständen → noch kein Vergleich): Termine als „ISO|Uhrzeit“, Schätzung, gebuchte Minuten */
  start?: string | null;
  due?: string | null;
  est?: number | null;
  spent?: number;
  /** 0.62: Vorgänger „gesamt|erledigt“ */
  deps?: string;
}

